/**
 * ServiceDesignerNewServiceModal — create a new Service from inside the
 * designer (spec §6/§22 "Nuevo servicio"). Reuses the existing Services API;
 * the created service is immediately opened in the graph editor.
 */
import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import './ServiceDesigner.css';

export default function ServiceDesignerNewServiceModal({ companyId, onClose, onCreated }) {
  const [meta, setMeta] = useState({ service_types: [], statuses: [] });
  const [name, setName] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [status, setStatus] = useState('running');
  const [description, setDescription] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    api.getServicesMeta(companyId)
      .then((m) => {
        setMeta(m);
        if (m.service_types && m.service_types.length) setServiceType(m.service_types[0].value);
        if (m.statuses && m.statuses.length && !m.statuses.some((s) => s.value === 'running')) {
          setStatus(m.statuses[0].value);
        }
      })
      .catch(() => {});
  }, [companyId]);

  const statuses = meta.statuses && meta.statuses.length
    ? meta.statuses
    : [{ value: 'running', label: 'Operativo' }, { value: 'planned', label: 'Planificado' }, { value: 'degraded', label: 'Degradado' }, { value: 'maintenance', label: 'Mantenimiento' }];

  const save = async () => {
    if (!name.trim()) { setError('El nombre del servicio es obligatorio.'); return; }
    setError(null);
    setSaving(true);
    try {
      const svc = await api.createService(
        { name: name.trim(), display_name: name.trim(), service_type: serviceType || 'other', status, criticality: 'medium', description: description.trim() || null },
        companyId
      );
      if (onCreated) onCreated(svc);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sd-modal-backdrop" onClick={onClose}>
      <div className="sd-modal sd-model-modal sd-nsvc" onClick={(e) => e.stopPropagation()}>
        <div className="sd-model-head">
          <h3>＋ Nuevo servicio</h3>
          <button className="sd-model-x" onClick={onClose} title="Cerrar"><XMarkIcon width={16} height={16} /></button>
        </div>
        <p className="sd-modal-sub">
          Crea el contenedor y ábrelo en el diseñador para añadirle CIs y relaciones.
        </p>

        {error && <div className="sd-error-banner">{error}<button onClick={() => setError(null)}><XMarkIcon width={14} height={14} /></button></div>}

        <label className="sd-builder-label">Nombre *</label>
        <input className="sd-builder-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="p. ej. Portal Clientes" autoFocus />

        <label className="sd-builder-label">Tipo</label>
        <select className="sd-builder-input" value={serviceType} onChange={(e) => setServiceType(e.target.value)}>
          {(meta.service_types || []).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>

        <label className="sd-builder-label">Estado</label>
        <select className="sd-builder-input" value={status} onChange={(e) => setStatus(e.target.value)}>
          {statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>

        <label className="sd-builder-label">Descripción (opcional)</label>
        <textarea className="sd-builder-input sd-builder-area" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Opcional" />

        <div className="sd-modal-actions" style={{ marginTop: 16 }}>
          <button className="sd-cancel-btn" onClick={onClose}>Cancelar</button>
          <button className="sd-save-btn" disabled={saving} onClick={save}>
            {saving ? 'Creando…' : 'Crear y abrir diseñador'}
          </button>
        </div>
      </div>
    </div>
  );
}
